import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type AppRole = 
  | 'super_admin'
  | 'admin'
  | 'manager'
  | 'sales_manager'
  | 'sales'
  | 'hr_manager'
  | 'hr'
  | 'accountant'
  | 'receptionist'
  | 'user';

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  assigned_by: string | null;
  assigned_at: string;
  is_active: boolean;
  expires_at: string | null;
  metadata: any;
}

// Hook للحصول على أدوار المستخدم
export function useUserRoles(userId?: string) {
  return useQuery({
    queryKey: ['user-roles', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      // TODO: استبدال بـ RPC بعد تطبيق Migration
      // حالياً نرجع array فارغ لأن الجدول غير موجود بعد
      console.log('User roles system requires migration to be applied first');
      return [];
    },
    enabled: !!userId
  });
}

// Hook لتعيين دور لمستخدم
export function useAssignUserRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      userId, 
      role, 
      expiresAt 
    }: { 
      userId: string; 
      role: AppRole; 
      expiresAt?: string;
    }) => {
      // TODO: استبدال بـ RPC بعد تطبيق Migration
      console.log('Assign user role requires migration:', { userId, role });
      toast.info('سيتم تفعيل هذه الميزة بعد تطبيق Migration');
      return { user_id: userId, role };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-roles', data.user_id] });
    },
    onError: (error: any) => {
      console.error('Error assigning role:', error);
      toast.error(error.message || 'فشل تعيين الدور');
    }
  });
}

// Hook لإلغاء تفعيل دور
export function useDeactivateUserRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AppRole }) => {
      // TODO: استبدال بـ RPC بعد تطبيق Migration
      console.log('Deactivate user role requires migration:', { userId, role });
      return { user_id: userId, role };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-roles', data.user_id] });
    },
    onError: (error: any) => {
      console.error('Error deactivating role:', error);
      toast.error(error.message || 'فشل إلغاء الدور');
    }
  });
}

// Hook للحصول على تدرج الأدوار
export function useRoleHierarchy() {
  return useQuery({
    queryKey: ['role-hierarchy'],
    queryFn: async () => {
      // سنستخدم بيانات ثابتة حتى يتم تطبيق Migration
      return [
        { role: 'super_admin', level: 100, inherits_from: ['admin', 'manager', 'hr_manager', 'sales_manager'] },
        { role: 'admin', level: 90, inherits_from: ['manager', 'hr_manager', 'sales_manager'] },
        { role: 'manager', level: 70, inherits_from: ['sales', 'hr', 'accountant'] },
        { role: 'sales_manager', level: 60, inherits_from: ['sales'] },
        { role: 'hr_manager', level: 60, inherits_from: ['hr'] },
        { role: 'accountant', level: 50, inherits_from: ['user'] },
        { role: 'sales', level: 40, inherits_from: ['user'] },
        { role: 'hr', level: 40, inherits_from: ['user'] },
        { role: 'receptionist', level: 30, inherits_from: ['user'] },
        { role: 'user', level: 10, inherits_from: null }
      ];
    }
  });
}

// Helper function للتحقق من الدور
export function hasRole(roles: UserRole[] | undefined, role: AppRole): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(r => r.role === role);
}

// Helper function للتحقق من أي دور
export function hasAnyRole(roles: UserRole[] | undefined, rolesList: AppRole[]): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(r => rolesList.includes(r.role));
}

// Helper للحصول على أعلى مستوى دور
export function getHighestRoleLevel(roles: UserRole[] | undefined): number {
  if (!roles || roles.length === 0) return 0;
  
  const roleLevels: Record<AppRole, number> = {
    'super_admin': 100,
    'admin': 90,
    'manager': 70,
    'sales_manager': 60,
    'hr_manager': 60,
    'accountant': 50,
    'sales': 40,
    'hr': 40,
    'receptionist': 30,
    'user': 10
  };
  
  return Math.max(...roles.map(r => roleLevels[r.role] || 0));
}

// الأدوار باللغة العربية
export const ROLE_LABELS: Record<AppRole, string> = {
  'super_admin': 'مدير النظام الرئيسي',
  'admin': 'مدير عام',
  'manager': 'مدير قسم',
  'sales_manager': 'مدير مبيعات',
  'sales': 'موظف مبيعات',
  'hr_manager': 'مدير موارد بشرية',
  'hr': 'موظف موارد بشرية',
  'accountant': 'محاسب',
  'receptionist': 'موظف استقبال',
  'user': 'مستخدم عادي'
};
